/**
 * Painel de Alerta de Novos Pedidos
 *
 * Exibe os dados dos novos pedidos que chegaram com ações diretas
 * para aceitar, recusar, imprimir e ver detalhes completos.
 *
 * @author Equipe VendeAi
 * @since 1.0.0
 */

import { useState } from 'react';
import {
  Bell,
  BellOff,
  CheckCircle,
  XCircle,
  Printer,
  ChevronDown,
  ChevronUp,
  User,
  Phone,
  MapPin,
  ShoppingBag,
  Clock,
  CreditCard,
  CalendarClock,
  Utensils,
} from 'lucide-react';
import { Button } from '@components/ui/Button';
import { Badge } from '@components/ui/Badge';
import { Modal } from '@components/ui/Modal';
import { cn } from '@utils/helpers';
import {
  formatCurrency,
  formatOrderNumber,
  formatRelativeTime,
  formatPhone,
} from '@utils/formatters';
import {
  ORDER_TYPE_LABELS,
  PAYMENT_METHOD_LABELS,
  SALES_CHANNEL_LABELS,
} from '@utils/constants';
import type { Order, OrderStatus } from '@/types/order';

interface NewOrderAlertPanelProps {
  /** Pedidos alertando (com dados completos quando disponíveis) */
  orders: Order[];
  /** Callback para atualizar status (aceitar/recusar) */
  onUpdateStatus: (orderId: string, status: OrderStatus, reason?: string) => Promise<void>;
  /** Callback para imprimir pedido */
  onPrintOrder: (order: Order) => void;
  /** Silencia todos os alertas */
  onDismissAll: () => void;
  /** ID do pedido cuja impressão está carregando */
  loadingReceiptOrderId?: string | null;
}

/**
 * Card individual de um pedido no painel de alerta
 */
function AlertOrderCard({
  order,
  onUpdateStatus,
  onPrintOrder,
  loadingReceiptOrderId,
}: {
  order: Order;
  onUpdateStatus: NewOrderAlertPanelProps['onUpdateStatus'];
  onPrintOrder: NewOrderAlertPanelProps['onPrintOrder'];
  loadingReceiptOrderId?: string | null;
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  const customerName = order.customer?.name || order.customerName || 'Cliente não identificado';
  const customerPhone = order.customer?.phone || order.customerPhone;
  const orderNumber = order.number ?? (order.orderNumber ? Number(order.orderNumber) : 0);

  const handleAccept = async () => {
    setProcessing(true);
    try {
      await onUpdateStatus(order.id, 'CONFIRMED');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    setProcessing(true);
    try {
      await onUpdateStatus(order.id, 'CANCELLED', cancelReason || undefined);
    } finally {
      setProcessing(false);
      setShowCancelModal(false);
      setCancelReason('');
    }
  };

  return (
    <>
      <div className="rounded-lg border-2 border-orange-300 bg-white shadow-md dark:border-orange-600 dark:bg-gray-800">
        {/* Cabeçalho do pedido */}
        <div className="flex items-center justify-between border-b border-orange-200 bg-orange-50 px-4 py-3 dark:border-orange-800 dark:bg-orange-900/20">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-orange-200 dark:bg-orange-800">
              <ShoppingBag size={18} className="text-orange-700 dark:text-orange-300" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-lg font-bold text-gray-900 dark:text-white">
                  #{formatOrderNumber(orderNumber)}
                </span>
                <Badge variant="secondary" className="text-[10px]">
                  {ORDER_TYPE_LABELS[order.type] || order.type}
                </Badge>
                {order.channel && SALES_CHANNEL_LABELS[order.channel as keyof typeof SALES_CHANNEL_LABELS] && (
                  <Badge variant="secondary" className="text-[10px]">
                    {SALES_CHANNEL_LABELS[order.channel as keyof typeof SALES_CHANNEL_LABELS]}
                  </Badge>
                )}
                {order.isScheduled && (
                  <span className="inline-flex items-center gap-0.5 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                    <CalendarClock size={10} />
                    Agendado
                  </span>
                )}
              </div>
              {order.createdAt && (
                <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                  <Clock size={11} />
                  <span>{formatRelativeTime(order.createdAt)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Botão expandir/recolher */}
          <button
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-orange-200 hover:text-gray-700 dark:hover:bg-orange-800 dark:hover:text-gray-300"
            title={isExpanded ? 'Recolher detalhes' : 'Expandir detalhes'}
          >
            {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </button>
        </div>

        {/* Informações do cliente e resumo */}
        <div className="px-4 py-3">
          <div className="flex items-start justify-between gap-4">
            {/* Dados do cliente */}
            <div className="flex-1 space-y-1">
              <div className="flex items-center gap-1.5 text-sm text-gray-700 dark:text-gray-300">
                <User size={14} className="flex-shrink-0 text-gray-500 dark:text-gray-400" />
                <span className="font-medium">{customerName}</span>
              </div>
              {customerPhone && (
                <div className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400">
                  <Phone size={14} className="flex-shrink-0" />
                  <span>{formatPhone(customerPhone)}</span>
                </div>
              )}
              {order.type === 'DELIVERY' && order.deliveryAddress && (
                <div className="flex items-start gap-1.5 text-sm text-gray-500 dark:text-gray-400">
                  <MapPin size={14} className="mt-0.5 flex-shrink-0" />
                  <span>
                    {order.deliveryAddress.street}, {order.deliveryAddress.number}
                    {order.deliveryAddress.neighborhood && ` - ${order.deliveryAddress.neighborhood}`}
                  </span>
                </div>
              )}
              {order.type === 'DINE_IN' && order.tableNumber && (
                <div className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400">
                  <Utensils size={14} className="flex-shrink-0" />
                  <span>Mesa {order.tableNumber}</span>
                </div>
              )}
            </div>

            {/* Total */}
            <div className="text-right">
              {order.paymentMethod && (
                <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                  <CreditCard size={12} />
                  <span>{PAYMENT_METHOD_LABELS[order.paymentMethod] || order.paymentMethod}</span>
                </div>
              )}
              <span className="text-xl font-bold text-primary-700 dark:text-primary-400">
                {formatCurrency(order.total || 0)}
              </span>
            </div>
          </div>

          {/* Resumo dos itens (sempre visível) */}
          {order.items && order.items.length > 0 && (
            <div className="mt-3 rounded-lg bg-gray-50 p-2.5 dark:bg-gray-900/50">
              <div className="space-y-1">
                {(isExpanded ? order.items : order.items.slice(0, 3)).map((item, idx) => (
                  <div key={item.id || idx} className="flex items-center justify-between text-sm">
                    <span className="text-gray-700 dark:text-gray-300">
                      <span className="font-medium">{item.quantity}x</span>{' '}
                      {item.productName || 'Produto'}
                      {item.options?.sizeName && (
                        <span className="text-xs text-gray-500 dark:text-gray-400"> ({item.options.sizeName})</span>
                      )}
                    </span>
                    <span className="text-gray-500 dark:text-gray-400">
                      {formatCurrency(item.total || item.unitPrice * item.quantity)}
                    </span>
                  </div>
                ))}
                {!isExpanded && order.items.length > 3 && (
                  <button
                    type="button"
                    onClick={() => setIsExpanded(true)}
                    className="text-xs font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400"
                  >
                    + {order.items.length - 3} {order.items.length - 3 === 1 ? 'item' : 'itens'}
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Detalhes expandidos */}
          {isExpanded && (
            <div className="mt-3 space-y-2 border-t border-gray-100 pt-3 dark:border-gray-700">
              {/* Add-ons dos itens */}
              {order.items?.some(item => item.addons && item.addons.length > 0) && (
                <div className="space-y-1">
                  {order.items.filter(item => item.addons && item.addons.length > 0).map((item, idx) => (
                    <div key={idx} className="text-xs text-gray-500 dark:text-gray-400">
                      <span className="font-medium">{item.productName}:</span>{' '}
                      {item.addons!.map(a => `${a.itemName}${a.quantity > 1 ? ` x${a.quantity}` : ''}`).join(', ')}
                    </div>
                  ))}
                </div>
              )}

              {/* Composição do total */}
              {(order.deliveryFee > 0 || order.discount > 0 || order.serviceFee > 0) && (
                <div className="space-y-0.5 text-xs text-gray-500 dark:text-gray-400">
                  {order.subtotal > 0 && (
                    <div className="flex justify-between">
                      <span>Subtotal</span>
                      <span>{formatCurrency(order.subtotal)}</span>
                    </div>
                  )}
                  {order.discount > 0 && (
                    <div className="flex justify-between text-success-700 dark:text-success-400">
                      <span>Desconto</span>
                      <span>-{formatCurrency(order.discount)}</span>
                    </div>
                  )}
                  {order.deliveryFee > 0 && (
                    <div className="flex justify-between">
                      <span>Taxa de entrega</span>
                      <span>{formatCurrency(order.deliveryFee)}</span>
                    </div>
                  )}
                  {order.serviceFee > 0 && (
                    <div className="flex justify-between">
                      <span>Taxa de serviço</span>
                      <span>{formatCurrency(order.serviceFee)}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Endereço completo para delivery */}
              {order.type === 'DELIVERY' && order.deliveryAddress && (
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  <span className="font-medium">Endereço completo:</span>{' '}
                  {order.deliveryAddress.street}, {order.deliveryAddress.number}
                  {order.deliveryAddress.complement && ` - ${order.deliveryAddress.complement}`}
                  {order.deliveryAddress.neighborhood && `, ${order.deliveryAddress.neighborhood}`}
                  {order.deliveryAddress.city && ` - ${order.deliveryAddress.city}`}
                  {order.deliveryAddress.zipCode && ` (${order.deliveryAddress.zipCode})`}
                </div>
              )}

              {/* Notas do pedido */}
              {order.notes && (
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  <span className="font-medium">Obs:</span> {order.notes}
                </div>
              )}

              {/* Troco */}
              {order.paymentMethod === 'CASH' && order.changeFor && order.changeFor > 0 && (
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  <span className="font-medium">Troco para:</span> {formatCurrency(order.changeFor)}
                </div>
              )}

              {/* Agendamento */}
              {order.isScheduled && order.scheduledDate && (
                <div className="flex items-center gap-1 text-xs text-amber-700 dark:text-amber-400">
                  <CalendarClock size={12} />
                  <span className="font-medium">
                    Agendado para {new Date(order.scheduledDate).toLocaleDateString('pt-BR', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Botões de ação */}
        <div className="flex items-center gap-2 border-t border-orange-200 bg-gray-50 px-4 py-3 dark:border-orange-800 dark:bg-gray-900/30">
          <Button
            variant="primary"
            size="sm"
            onClick={handleAccept}
            isLoading={processing}
            disabled={processing}
            className="flex-1"
          >
            <CheckCircle size={16} />
            Aceitar Pedido
          </Button>
          <Button
            variant="danger"
            size="sm"
            onClick={() => setShowCancelModal(true)}
            disabled={processing}
            className="flex-1"
          >
            <XCircle size={16} />
            Recusar
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPrintOrder(order)}
            disabled={loadingReceiptOrderId === order.id}
            title="Imprimir cupom"
          >
            <Printer size={16} />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            title={isExpanded ? 'Recolher detalhes' : 'Expandir detalhes'}
          >
            {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </Button>
        </div>
      </div>

      {/* Modal de confirmação de recusa */}
      <Modal
        isOpen={showCancelModal}
        onClose={() => { setShowCancelModal(false); setCancelReason(''); }}
        title="Recusar Pedido"
        size="sm"
        zIndex="z-[110]"
        footer={
          <>
            <Button variant="outline" onClick={() => { setShowCancelModal(false); setCancelReason(''); }}>
              Voltar
            </Button>
            <Button
              variant="danger"
              onClick={handleReject}
              isLoading={processing}
            >
              Confirmar Recusa
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <p className="text-gray-600 dark:text-gray-400">
            Tem certeza que deseja recusar o pedido <strong>#{formatOrderNumber(orderNumber)}</strong>?
          </p>
          <div>
            <label
              htmlFor={`cancel-reason-${order.id}`}
              className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              Motivo da recusa (opcional)
            </label>
            <textarea
              id={`cancel-reason-${order.id}`}
              rows={3}
              value={cancelReason}
              onChange={e => setCancelReason(e.target.value)}
              placeholder="Informe o motivo da recusa..."
              className="w-full rounded-lg border border-gray-300 p-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            />
          </div>
        </div>
      </Modal>
    </>
  );
}

/**
 * Painel de alerta de novos pedidos
 */
export function NewOrderAlertPanel({
  orders,
  onUpdateStatus,
  onPrintOrder,
  onDismissAll,
  loadingReceiptOrderId,
}: NewOrderAlertPanelProps) {
  if (orders.length === 0) return null;

  return (
    <div className="space-y-3">
      {/* Cabeçalho do painel */}
      <div className={cn(
        'flex items-center justify-between rounded-lg px-4 py-3',
        'bg-gradient-to-r from-orange-500 to-orange-600',
        'shadow-lg shadow-orange-500/20',
      )}>
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-white/20">
            <Bell size={20} className="animate-bounce text-white" />
          </div>
          <div>
            <p className="font-bold text-white">
              {orders.length === 1
                ? 'Novo pedido aguardando!'
                : `${orders.length} novos pedidos aguardando!`}
            </p>
            <p className="text-xs text-orange-100">
              Aceite ou recuse para parar o alerta sonoro
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={onDismissAll}
          title="Silenciar alerta"
          className="border-white/30 bg-white/10 text-white hover:bg-white/20"
        >
          <BellOff size={16} />
          <span className="ml-1 hidden sm:inline">Silenciar</span>
        </Button>
      </div>

      {/* Cards dos pedidos */}
      {orders.map(order => (
        <AlertOrderCard
          key={order.id}
          order={order}
          onUpdateStatus={onUpdateStatus}
          onPrintOrder={onPrintOrder}
          loadingReceiptOrderId={loadingReceiptOrderId}
        />
      ))}
    </div>
  );
}
